import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import {
  FACADE_SERVICE_TYPES, FACADE_TYPES, MONTAGE_INSTRUCTION, NOT_APPLICABLE, SELECT_CONSTRUCTION,
  SELECT_INSTALLATION, TRANSPORT_OPTIONS,
} from './constants';
import { errorMessage } from './errors';
import InstructionActions from './InstructionActions';
import {
  calculateMontagePreview, computeMontageDisplayRows, computeMontageFinancials,
  computeMontagePajaczekRow, computeMontageServices, computeMontageTransportRow,
  constructionPartsFromDescription, joinConstructionParts,
} from './montage';
import { localizedNumber, money, polishNoun } from './pricing';
import { IDLE_STYLES as styles } from './styles';
import useQueueStatus from './useQueueStatus';

const MontageSection = ({ onOpenInstruction }) => {
  const montageFileInputRef = useRef(null);
  const montageRequestRef = useRef(0);
  const montageExportRef = useRef(false);
  const [montageFile, setMontageFile] = useState(null);
  const [montageSourceUrl, setMontageSourceUrl] = useState('');
  const [montageResultUrl, setMontageResultUrl] = useState('');
  const [montageResultName, setMontageResultName] = useState('wycena_montazu.pdf');
  const [montagePreviewMode, setMontagePreviewMode] = useState('source');
  const [montageAnalysis, setMontageAnalysis] = useState(null);
  const [montageRegion, setMontageRegion] = useState('pl');
  const [montageSelections, setMontageSelections] = useState({});
  const [montageDiscountMode, setMontageDiscountMode] = useState('general_discount');
  const [montageGeneralDiscount, setMontageGeneralDiscount] = useState('0');
  const [montageTargetGross, setMontageTargetGross] = useState('');
  const [montageVat, setMontageVat] = useState('23');
  const [montageShowDiscountInPdf, setMontageShowDiscountInPdf] = useState(false);
  const [montageDemolition, setMontageDemolition] = useState(null);
  const [montageDemolitionDisposal, setMontageDemolitionDisposal] = useState(null);
  const [montageTransport, setMontageTransport] = useState(null);
  const [montageTransportType, setMontageTransportType] = useState('');
  const [montageTransportKm, setMontageTransportKm] = useState('');
  const [montagePajaczek, setMontagePajaczek] = useState(null);
  const [montagePajaczekDays, setMontagePajaczekDays] = useState('');
  const [montageBusy, setMontageBusy] = useState(false);
  const [montageExportBusy, setMontageExportBusy] = useState(false);
  const [montageExportAttempted, setMontageExportAttempted] = useState(false);
  const [montageMessage, setMontageMessage] = useState(null);
  const [montageReviewedDimensionRows, setMontageReviewedDimensionRows] = useState(
    () => new Set(),
  );
  const queue = useQueueStatus(montageBusy);

  const montageServices = useMemo(() => computeMontageServices({
    montageAnalysis, montageRegion, montageDemolition, montageDemolitionDisposal,
  }), [montageAnalysis, montageRegion, montageDemolition, montageDemolitionDisposal]);

  const montageDisplayRows = useMemo(
    () => computeMontageDisplayRows({ montageAnalysis, montageServices }),
    [montageAnalysis, montageServices],
  );

  const montageTransportPrice = montageAnalysis?.configurations?.[montageRegion]
    ?.service_prices?.[montageTransportType];
  const montageTransportKmNumber = localizedNumber(montageTransportKm);
  const montageTransportKmValid = Number.isFinite(montageTransportKmNumber)
    && montageTransportKmNumber > 0 && montageTransportKmNumber <= 100000;
  const montageTransportRow = useMemo(() => computeMontageTransportRow({
    montageTransport, montageTransportType, montageTransportPrice,
    montageTransportKmNumber, montageTransportKmValid,
  }), [montageTransport, montageTransportType, montageTransportPrice, montageTransportKmNumber, montageTransportKmValid]);

  const montagePajaczekDayRate = montageAnalysis?.configurations?.[montageRegion]?.service_prices?.PAJACZEK_DZIEN;
  const montagePajaczekKmRate = montageAnalysis?.configurations?.[montageRegion]?.service_prices?.PAJACZEK_KM;
  const montagePajaczekDaysNumber = Number(montagePajaczekDays);
  const montagePajaczekDaysValid = /^\d+$/.test(montagePajaczekDays)
    && Number.isSafeInteger(montagePajaczekDaysNumber)
    && montagePajaczekDaysNumber >= 1 && montagePajaczekDaysNumber <= 100000;
  const montagePajaczekRow = useMemo(() => computeMontagePajaczekRow({
    montagePajaczek, montageTransportRow, montagePajaczekDaysValid, montagePajaczekDayRate,
    montagePajaczekKmRate, montagePajaczekDaysNumber, montageTransportKmNumber,
  }), [montagePajaczek, montageTransportRow, montagePajaczekDaysValid,
    montagePajaczekDayRate, montagePajaczekKmRate, montagePajaczekDaysNumber, montageTransportKmNumber]);

  const montageFinancials = useMemo(() => computeMontageFinancials({
    montageDisplayRows, montageTransportRow, montagePajaczekRow, montageDiscountMode,
    montageGeneralDiscount, montageTargetGross, montageVat,
  }), [montageDisplayRows, montageTransportRow, montagePajaczekRow,
    montageDiscountMode, montageGeneralDiscount, montageTargetGross, montageVat]);

  useEffect(() => {
    if (!montageFile) { setMontageSourceUrl(''); return undefined; }
    const url = URL.createObjectURL(montageFile);
    setMontageSourceUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [montageFile]);

  useEffect(() => () => {
    if (montageResultUrl) URL.revokeObjectURL(montageResultUrl);
  }, [montageResultUrl]);

  const finish = () => {
    montageRequestRef.current += 1;
    montageExportRef.current = false;
    setMontageFile(null);
    setMontageAnalysis(null);
    setMontageSelections({});
    setMontageResultUrl('');
    setMontageResultName('wycena_montazu.pdf');
    setMontagePreviewMode('source');
    setMontageRegion('pl');
    setMontageDiscountMode('general_discount');
    setMontageGeneralDiscount('0');
    setMontageTargetGross('');
    setMontageVat('23');
    setMontageShowDiscountInPdf(false);
    setMontageDemolition(null);
    setMontageDemolitionDisposal(null);
    setMontageTransport(null);
    setMontageTransportType('');
    setMontageTransportKm('');
    setMontagePajaczek(null);
    setMontagePajaczekDays('');
    setMontageBusy(false);
    setMontageExportBusy(false);
    setMontageExportAttempted(false);
    setMontageMessage(null);
    setMontageReviewedDimensionRows(new Set());
    if (montageFileInputRef.current) montageFileInputRef.current.value = '';
  };

  const chooseMontageFile = event => {
    montageRequestRef.current += 1;
    setMontageFile(event.target.files?.[0] || null);
    setMontageAnalysis(null);
    setMontageSelections({});
    setMontageDiscountMode('general_discount');
    setMontageGeneralDiscount('0');
    setMontageTargetGross('');
    setMontageVat('23');
    setMontageShowDiscountInPdf(false);
    setMontageDemolition(null);
    setMontageDemolitionDisposal(null);
    setMontageTransport(null);
    setMontageTransportType('');
    setMontageTransportKm('');
    setMontagePajaczek(null);
    setMontagePajaczekDays('');
    setMontageRegion('pl');
    setMontageResultUrl('');
    setMontagePreviewMode('source');
    setMontageExportAttempted(false);
    setMontageMessage(null);
    setMontageReviewedDimensionRows(new Set());
  };

  const analyzeMontage = async () => {
    if (!montageFile) return;
    const requestId = ++montageRequestRef.current;
    setMontageExportAttempted(false);
    setMontageBusy(true);
    setMontageMessage({ type: 'info', text: 'Analiza montażu oczekuje w kolejce…' });
    const form = new FormData();
    form.append('file', montageFile);
    form.append('region', montageRegion);
    form.append('selections', '{}');
    try {
      const { data } = await api.post('/aluminium-deals/montage/analyze', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (requestId !== montageRequestRef.current) return;
      const normalized = Object.fromEntries(data.rows.map(row => [row.row_id, {
        installation_type: row.installation_type,
        construction_type: row.construction_type,
        width_m: row.width_m,
        height_m: row.height_m,
        quantity: String(row.quantity),
      }]));
      setMontageSelections(normalized);
      setMontageAnalysis(data);
      setMontageReviewedDimensionRows(new Set());
      setMontageResultUrl('');
      setMontageMessage({
        type: 'success',
        text: `Rozpoznano ${data.count} ${polishNoun(data.count, 'pozycję', 'pozycje', 'pozycji')} i wyceniono ${data.construction_count} ${polishNoun(data.construction_count, 'konstrukcję', 'konstrukcje', 'konstrukcji')}.`,
      });
    } catch (error) {
      if (requestId === montageRequestRef.current) {
        setMontageMessage({ type: 'error', text: await errorMessage(error) });
      }
    } finally {
      if (requestId === montageRequestRef.current) setMontageBusy(false);
    }
  };

  const changeMontageRegion = nextRegion => {
    const preview = calculateMontagePreview(montageAnalysis, nextRegion, montageSelections);
    if (!preview) return;
    setMontageRegion(nextRegion);
    if (nextRegion === 'eu' && montageDemolitionDisposal) setMontageDemolitionDisposal(null);
    setMontageSelections(preview.selections);
    setMontageAnalysis(preview);
    setMontageResultUrl('');
    setMontageMessage({ type: 'success', text: 'Wycena została przeliczona.' });
  };

  const changeMontageSelection = (rowId, field, value) => {
    const next = {
      ...montageSelections,
      [rowId]: { ...montageSelections[rowId], [field]: value },
    };
    const preview = calculateMontagePreview(montageAnalysis, montageRegion, next);
    if (!preview) return;
    setMontageSelections(preview.selections);
    setMontageAnalysis(preview);
    setMontageResultUrl('');
    setMontageMessage({ type: 'success', text: 'Wycena została przeliczona.' });
  };

  const changeAllMontageInstallations = value => {
    if (!value) return;
    const next = { ...montageSelections };
    montageAnalysis.rows.forEach(row => {
      if (FACADE_TYPES.has(row.construction_type)) return;
      next[row.row_id] = {
        ...next[row.row_id],
        installation_type: value,
      };
    });
    const preview = calculateMontagePreview(montageAnalysis, montageRegion, next);
    if (!preview) return;
    setMontageSelections(preview.selections);
    setMontageAnalysis(preview);
    setMontageResultUrl('');
    setMontagePreviewMode('source');
    setMontageMessage({
      type: 'success',
      text: 'Ustawiono typ montażu dla wszystkich konstrukcji.',
    });
  };

  const changeMontageDimension = (rowId, field, value) => {
    if (!/^\d{0,3}(?:[.,]\d{0,3})?$/.test(value)) return;
    const row = montageAnalysis?.rows.find(item => item.row_id === rowId);
    const previousValue = localizedNumber(row?.[field]);
    const nextValue = localizedNumber(value);
    if (
      value !== ''
      && Number.isFinite(nextValue)
      && nextValue > 0
      && nextValue <= 100
      && nextValue !== previousValue
    ) {
      setMontageReviewedDimensionRows(previous => {
        const next = new Set(previous);
        next.add(rowId);
        return next;
      });
    }
    changeMontageSelection(rowId, field, value);
  };

  const changeMontageQuantity = (rowId, value) => {
    if (!/^\d{0,3}$/.test(value)) return;
    changeMontageSelection(rowId, 'quantity', value);
  };

  const changeMontageDiscountMode = nextMode => {
    setMontageDiscountMode(nextMode);
    setMontageGeneralDiscount('0');
    setMontageTargetGross('');
    setMontageResultUrl('');
    setMontagePreviewMode('source');
  };

  const addMontageRow = sourceRow => {
    const rowId = `${sourceRow.source_page}:${sourceRow.position}:custom:${Date.now()}`;
    const sourceRowId = sourceRow.source_row_id || sourceRow.row_id;
    const row = { ...sourceRow, row_id: rowId, source_row_id: sourceRowId, quantity: 1, added: true };
    const rows = [...montageAnalysis.rows];
    const insertionIndex = rows.reduce(
      (last, item, index) => (item.position === sourceRow.position ? index : last),
      -1,
    ) + 1;
    rows.splice(insertionIndex, 0, row);
    const analysis = { ...montageAnalysis, rows };
    const next = {
      ...montageSelections,
      [rowId]: {
        installation_type: SELECT_INSTALLATION,
        construction_type: SELECT_CONSTRUCTION,
        width_m: sourceRow.width_m,
        height_m: sourceRow.height_m,
        quantity: '1',
        position: sourceRow.position,
        source_row_id: sourceRowId,
        added: true,
      },
    };
    const preview = calculateMontagePreview(analysis, montageRegion, next);
    setMontageSelections(preview.selections);
    setMontageAnalysis(preview);
    setMontageResultUrl('');
    setMontageMessage({ type: 'success', text: `Dodano wiersz do pozycji ${sourceRow.position}.` });
  };

  const removeMontageRow = row => {
    const { row_id: rowId } = row;
    const rows = montageAnalysis.rows.filter(row => row.row_id !== rowId);
    const next = { ...montageSelections };
    if (row.added) delete next[rowId];
    else next[rowId] = { deleted: true };
    const preview = calculateMontagePreview({ ...montageAnalysis, rows }, montageRegion, next);
    setMontageSelections(preview.selections);
    setMontageAnalysis(preview);
    setMontageResultUrl('');
  };

  const exportMontage = async () => {
    if (!montageFile || !montageAnalysis || montageExportRef.current) return;
    setMontageExportAttempted(true);
    if (montageExportIssues.length) return;
    setMontageExportAttempted(false);
    montageExportRef.current = true;
    setMontageBusy(true);
    setMontageExportBusy(true);
    setMontageMessage({ type: 'info', text: 'Eksport PDF oczekuje w kolejce…' });
    const form = new FormData();
    form.append('file', montageFile);
    form.append('region', montageRegion);
    form.append('selections', JSON.stringify(montageSelections));
    form.append('discount_mode', montageDiscountMode);
    form.append('discount_value', montageDiscountMode === 'target_gross' ? montageTargetGross : montageGeneralDiscount);
    form.append('vat_rate', montageVat);
    form.append('show_discount_in_pdf', String(montageShowDiscountInPdf));
    form.append('demolition', String(montageDemolition === true));
    form.append('demolition_disposal', String(montageDemolitionDisposal));
    form.append('transport', String(montageTransport));
    form.append('transport_type', montageTransport ? montageTransportType : '');
    form.append('transport_km', montageTransport ? montageTransportKm : '');
    form.append('pajaczek', String(montagePajaczek));
    form.append('pajaczek_days', montagePajaczek ? montagePajaczekDays : '');
    try {
      const response = await api.post('/aluminium-deals/montage/export', form, {
        headers: { 'Content-Type': 'multipart/form-data' }, responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      setMontageResultUrl(url);
      setMontagePreviewMode('result');
      const match = response.headers['content-disposition']?.match(/filename="?([^";]+)"?/);
      setMontageResultName(match?.[1] || 'wycena_montazu.pdf');
      setMontageMessage({ type: 'success', text: 'Gotowe — wycena montażu PDF jest dostępna poniżej.' });
    } catch (error) {
      setMontageMessage({ type: 'error', text: await errorMessage(error) });
    } finally {
      montageExportRef.current = false;
      setMontageBusy(false);
      setMontageExportBusy(false);
    }
  };

  const montageVatNumber = localizedNumber(montageVat);
  const montageDiscountNumber = localizedNumber(montageGeneralDiscount);
  const montageTargetNumber = localizedNumber(montageTargetGross);
  const montageRowsRequiringInstallation = (montageAnalysis?.rows || []).filter(
    row => !FACADE_TYPES.has(row.construction_type),
  );
  const montageInstallationTypes = new Set(
    montageRowsRequiringInstallation.map(row => row.installation_type),
  );
  const commonMontageInstallation = montageInstallationTypes.size === 1
    ? montageRowsRequiringInstallation[0]?.installation_type || ''
    : '';
  const montageHasUnselectedConstruction = (montageAnalysis?.rows || []).some(
    row => row.construction_type === SELECT_CONSTRUCTION,
  );
  const montageHasUnselectedInstallation = montageRowsRequiringInstallation.some(
    row => row.installation_type === SELECT_INSTALLATION,
  );
  const montageHasManualDemolition = (montageAnalysis?.rows || []).some(
    row => row.added && row.construction_type === 'DEMONTAŻ',
  );
  const montageExportIssues = [];
  const missingServices = [];
  if (montageDemolitionDisposal === null) missingServices.push('Demontaż + utylizacja');
  if (montageDemolitionDisposal !== true && montageDemolition === null) missingServices.push('Demontaż');
  if (montageTransport === null) missingServices.push('Transport');
  if (montagePajaczek === null) missingServices.push('Pajączek');
  if (missingServices.length) montageExportIssues.push(`Uzupełnij Usługi: ${missingServices.join(', ')}.`);
  if (montageTransport && (!montageTransportType || !montageTransportKmValid)) {
    montageExportIssues.push('Wybierz rodzaj transportu i podaj poprawną liczbę kilometrów.');
  }
  if (montageTransport && montageTransportType && montageTransportPrice === undefined) {
    montageExportIssues.push('Brak stawki transportu dla wybranego regionu.');
  }
  if (montagePajaczek && (!montageTransport || !montagePajaczekDaysValid)) {
    montageExportIssues.push('Pajączek wymaga transportu i poprawnej liczby dni.');
  }
  if (montagePajaczek && (montagePajaczekDayRate === undefined || montagePajaczekKmRate === undefined)) {
    montageExportIssues.push('Brak stawki dla usługi Pajączek.');
  }
  if (montageServices.unavailable) montageExportIssues.push('Brak stawki dla wybranych usług.');
  if ((montageDemolition || montageDemolitionDisposal) && montageHasManualDemolition) {
    montageExportIssues.push('Usuń ręcznie dodane wiersze demontażu.');
  }
  if (montageHasUnselectedConstruction) {
    montageExportIssues.push('Wybierz rodzaj konstrukcji we wszystkich pozycjach.');
  }
  if (montageHasUnselectedInstallation) {
    montageExportIssues.push('Wybierz typ montażu we wszystkich pozycjach, które go wymagają.');
  }
  const montageRows = montageAnalysis?.rows || [];
  const montageConfiguredPrices = montageAnalysis?.configurations?.[montageRegion]?.prices || {};
  const missingManualPrice = montageRegion === 'eu' && montageRows.some(row => {
    if (row.construction_type === SELECT_CONSTRUCTION
      || row.installation_type === SELECT_INSTALLATION
      || montageConfiguredPrices[`${row.installation_type}\u0000${row.construction_type}`] !== undefined) return false;
    const value = String(montageSelections[row.row_id]?.unit_price ?? '').trim();
    return !value || !Number.isFinite(localizedNumber(value)) || localizedNumber(value) < 0;
  });
  if (missingManualPrice) montageExportIssues.push('Podaj cenę jednostkową w pozycjach bez stawki UE.');
  if (montageRows.some(row => (
    (!montageHasUnselectedConstruction && !montageHasUnselectedInstallation && row.calculation_error)
    || !Number.isFinite(localizedNumber(row.width_m))
    || localizedNumber(row.width_m) <= 0
    || localizedNumber(row.width_m) > 100
    || !Number.isFinite(localizedNumber(row.height_m))
    || localizedNumber(row.height_m) <= 0
    || localizedNumber(row.height_m) > 100
    || !Number.isInteger(Number(row.quantity))
    || Number(row.quantity) < 1
    || Number(row.quantity) > 100
  ))) montageExportIssues.push('Popraw dane wierszy wyceny (wymiary, ilość lub cenę).');
  if (String(montageVat).trim() === '' || !Number.isFinite(montageVatNumber)
    || montageVatNumber < 0 || montageVatNumber > 23) {
    montageExportIssues.push('Podaj VAT od 0 do 23%.');
  }
  if (montageDiscountMode === 'general_discount' && (
    String(montageGeneralDiscount).trim() === ''
    || !Number.isFinite(montageDiscountNumber)
    || montageDiscountNumber < 0
    || montageDiscountNumber > 100
  )) montageExportIssues.push('Podaj rabat ogólny od 0 do 100%.');
  if (montageDiscountMode === 'target_gross' && (
    !Number.isFinite(montageTargetNumber)
    || montageTargetNumber <= 0
    || montageTargetNumber > montageFinancials.originalGross
    || montageFinancials.targetBelowFixedServices
  )) montageExportIssues.push('Podaj poprawną docelową wartość brutto.');

  const montageConstructionTypesByPosition = new Map();
  (montageAnalysis?.rows || []).forEach(row => {
    if (row.display_position) return;
    if (!montageConstructionTypesByPosition.has(row.position)) {
      montageConstructionTypesByPosition.set(row.position, new Set());
    }
    montageConstructionTypesByPosition.get(row.position).add(row.construction_type);
  });
  const montagePositionsRequiringDimensionReview = new Set(
    [...montageConstructionTypesByPosition.entries()]
      .filter(([, types]) => types.size > 1)
      .map(([position]) => position),
  );
  const montageMixedConstructionWarnings = new Map();
  (montageAnalysis?.rows || []).forEach(row => {
    if (montageMixedConstructionWarnings.has(row.position)) return;
    const parts = constructionPartsFromDescription(row.description);
    if (parts.length > 1) {
      montageMixedConstructionWarnings.set(row.position, parts);
    }
  });

  return <>
    <div className="aluminium-upload-panel" style={styles.panel}>
      <div className="aluminium-module-header">
        <div>
          <h3 className="aluminium-automation-title">Naliczanie montaży</h3>
        </div>
        <InstructionActions instruction={MONTAGE_INSTRUCTION} onPreview={onOpenInstruction} />
      </div>
      <div style={styles.row}>
        <label className={`aluminium-file-button ${montageBusy ? 'disabled' : ''}`} aria-disabled={montageBusy}>
          Wybierz ofertę
          <input ref={montageFileInputRef} className="aluminium-visually-hidden-input" type="file" accept="application/pdf,.pdf" onChange={chooseMontageFile} disabled={montageBusy} />
        </label>
        {montageFile && <span className="aluminium-selected-file">{montageFile.name}</span>}
        <button style={{ ...styles.button, cursor: montageBusy ? 'wait' : 'pointer' }} onClick={analyzeMontage} disabled={!montageFile || montageBusy}>{montageBusy ? 'Przetwarzanie…' : 'Analizuj'}</button>
        {montageBusy && <small>{queue.active ? `Kolejka pracuje (${queue.waiting} oczekuje)` : 'Oczekiwanie na kolejkę…'}</small>}
      </div>
      {montageMessage && <p style={{ color: montageMessage.type === 'error' ? '#b91c1c' : montageMessage.type === 'success' ? '#15803d' : '#1d4ed8' }}>{montageMessage.text}</p>}
    </div>

    {!montageAnalysis && !montageBusy && <section className="aluminium-faq" style={styles.panel} aria-labelledby="montage-faq-title">
      <h3 id="montage-faq-title">FAQ</h3>
      <details>
        <summary>OSTATNIE AKTUALIZACJE</summary>
        <div className="aluminium-updates">
          <p>
            <time dateTime="2026-08-11T18:28:09">11.08.2026 18:28:19</time>
            <span>
              Dla konstrukcji wymagających konsultacji w cenniku „EU” dodano
              możliwość samodzielnego wpisania ceny jednostkowej, np. dla
              „ŚCIANKI”.
            </span>
          </p>
          <p>
            <time dateTime="2026-08-11T18:28:09">11.08.2026 18:28:19</time>
            <span>Wdrożono cennik „Polska Fabryczny”.</span>
          </p>
          <p>
            <time dateTime="2026-08-09T19:32:06">09.08.2026 19:32:06</time>
            <span>Zaktualizowano cennik.</span>
          </p>
        </div>
      </details>
      <details>
        <summary>Jaki plik tutaj załączamy?</summary>
        <p>Należy załączyć plik z rysunkami konstrukcji (szablon Maciej&amp;Patryk).</p>
      </details>
      <details>
        <summary>Gdzie złosić błąd lub propozycję wdrożenia nowej funkcjonalności?</summary>
        <p>aberdowski@matplast.pl</p>
      </details>
    </section>}

    {montageAnalysis && <div className="aluminium-config-panel" style={styles.panel}>
      <div className="aluminium-montage-heading">
        <div>
          <h3>Wycena montażu</h3>
          <p>Najpierw wybierz region rozliczenia: Polska, Polska Fabryczny lub Unia Europejska. Następnie przeanalizuj, czy zgadzają się typy konstrukcji i dobierz odpowiednie typy montażu.</p>
        </div>
        <div className="aluminium-region-switcher" role="group" aria-label="Obszar rozliczenia montażu">
          <button type="button" className={`aluminium-mode-button ${montageRegion === 'pl' ? 'active' : ''}`} aria-pressed={montageRegion === 'pl'} onClick={() => changeMontageRegion('pl')} disabled={montageBusy}>🇵🇱 Polska</button>
          <button type="button" className={`aluminium-mode-button ${montageRegion === 'pl_fabr' ? 'active' : ''}`} aria-pressed={montageRegion === 'pl_fabr'} onClick={() => changeMontageRegion('pl_fabr')} disabled={montageBusy}>🇵🇱🏭 Polska Fabryczny</button>
          <button type="button" className={`aluminium-mode-button ${montageRegion === 'eu' ? 'active' : ''}`} aria-pressed={montageRegion === 'eu'} onClick={() => changeMontageRegion('eu')} disabled={montageBusy}>🇪🇺 Unia Europejska</button>
        </div>
      </div>
      <section className="aluminium-additional-services" aria-labelledby="aluminium-additional-services-title">
        <div className="aluminium-additional-services-heading">
          <div>
            <h4 id="aluminium-additional-services-title">Usługi</h4>
          </div>
          <span>4 usługi do wyboru</span>
        </div>
        <div className="aluminium-additional-services-grid">
          <fieldset className="aluminium-service-card">
            <legend>Demontaż + utylizacja <span aria-hidden="true">*</span></legend>
            <p>Obie usługi zostaną doliczone do pozycji.</p>
            <div className="aluminium-service-options">
              <label><input type="radio" name="montage-demolition-disposal" checked={montageDemolitionDisposal === true} onChange={() => { setMontageDemolitionDisposal(true); setMontageDemolition(null); setMontageResultUrl(''); }} disabled={montageBusy || montageRegion === 'eu'} /> Tak</label>
              <label><input type="radio" name="montage-demolition-disposal" checked={montageDemolitionDisposal === false} onChange={() => { setMontageDemolitionDisposal(false); setMontageResultUrl(''); }} disabled={montageBusy} /> Nie</label>
            </div>
          </fieldset>
          <fieldset className="aluminium-service-card">
            <legend>Demontaż {montageDemolitionDisposal !== true && <span aria-hidden="true">*</span>}</legend>
            <p>{montageDemolitionDisposal ? 'Demontaż jest już wliczony w wybrany wariant.' : 'Dodaj demontaż do pozycji w wycenie.'}</p>
            {montageDemolitionDisposal
              ? <div className="aluminium-service-included">Już uwzględniony</div>
              : <div className="aluminium-service-options">
                <label><input type="radio" name="montage-demolition" checked={montageDemolition === true} onChange={() => { setMontageDemolition(true); setMontageResultUrl(''); }} disabled={montageBusy} /> Tak</label>
                <label><input type="radio" name="montage-demolition" checked={montageDemolition === false} onChange={() => { setMontageDemolition(false); setMontageResultUrl(''); }} disabled={montageBusy} /> Nie</label>
              </div>}
          </fieldset>
          <fieldset className="aluminium-service-card">
            <legend>Transport <span aria-hidden="true">*</span></legend>
            <p>Koszt liczony według rodzaju pojazdu i trasy.</p>
            <div className="aluminium-service-options">
              <label><input type="radio" name="montage-transport" checked={montageTransport === true} onChange={() => { setMontageTransport(true); setMontageResultUrl(''); }} disabled={montageBusy} /> Tak</label>
              <label><input type="radio" name="montage-transport" checked={montageTransport === false} onChange={() => { setMontageTransport(false); setMontagePajaczek(false); setMontagePajaczekDays(''); setMontageResultUrl(''); }} disabled={montageBusy} /> Nie</label>
            </div>
            {montageTransport && <div className="aluminium-transport-controls">
              <label htmlFor="montage-transport-type">Rodzaj transportu
                <select id="montage-transport-type" value={montageTransportType} onChange={event => { setMontageTransportType(event.target.value); setMontageResultUrl(''); }} disabled={montageBusy}>
                  <option value="">Wybierz rodzaj transportu</option>
                  {TRANSPORT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
              <label htmlFor="montage-transport-km">Kilometry łącznie tam i z powrotem
                <input id="montage-transport-km" type="text" inputMode="decimal" value={montageTransportKm} onChange={event => { setMontageTransportKm(event.target.value); setMontageResultUrl(''); }} disabled={montageBusy} />
              </label>
              <p>Transport liczony za każdy kilometr z punktu A (Firma Matplast) do punktu B (miejsce dostawy lub montażu zlecenia) i z powrotem.</p>
              {(!montageTransportType || !montageTransportKmValid) && <p className="aluminium-form-error">Wybierz rodzaj transportu i podaj liczbę kilometrów większą od 0, maksymalnie 100 000.</p>}
              {montageTransportType && montageTransportPrice === undefined && <p className="aluminium-form-error">Brak stawki transportu dla wybranego regionu.</p>}
            </div>}
          </fieldset>
          <fieldset className="aluminium-service-card">
            <legend>Pajączek <span aria-hidden="true">*</span></legend>
            <p>Wymaga transportu; koszt zależy od dni i kilometrów.</p>
            <div className="aluminium-service-options">
              <label><input type="radio" name="montage-pajaczek" checked={montagePajaczek === true} onChange={() => { setMontagePajaczek(true); setMontageResultUrl(''); }} disabled={montageBusy || montageTransport !== true} /> Tak</label>
              <label><input type="radio" name="montage-pajaczek" checked={montagePajaczek === false} onChange={() => { setMontagePajaczek(false); setMontageResultUrl(''); }} disabled={montageBusy} /> Nie</label>
            </div>
            {montageTransport === false && <p className="aluminium-service-hint">Wybierz transport „Tak”, aby dodać Pajączka.</p>}
            {montagePajaczek && <div className="aluminium-transport-controls">
              <label htmlFor="montage-pajaczek-days">Liczba dni
                <input id="montage-pajaczek-days" type="number" min="1" max="100000" step="1" value={montagePajaczekDays} onChange={event => { setMontagePajaczekDays(event.target.value); setMontageResultUrl(''); }} disabled={montageBusy} />
              </label>
              {montagePajaczekDayRate !== undefined && montagePajaczekKmRate !== undefined && <p>Stawka: {money(montagePajaczekDayRate)} za dzień oraz {money(montagePajaczekKmRate)} za każdy kilometr podany przy transporcie.</p>}
              {!montagePajaczekDaysValid && <p className="aluminium-form-error">Podaj całkowitą liczbę dni od 1 do 100 000.</p>}
              {(montagePajaczekDayRate === undefined || montagePajaczekKmRate === undefined) && <p className="aluminium-form-error">Brak stawki dla usługi Pajączek.</p>}
            </div>}
          </fieldset>
        </div>
        {montageRegion === 'eu' && <p className="aluminium-form-error">Utylizacja jest niedostępna dla UE, ponieważ w cenniku nie ma stawek dla tego regionu.</p>}
        {((montageDemolitionDisposal !== true && montageDemolition === null) || montageDemolitionDisposal === null || montageTransport === null || montagePajaczek === null) && <p className="aluminium-form-error">Odpowiedz na wymagane pytania, aby wyeksportować wycenę.</p>}
        {(montageDemolition || montageDemolitionDisposal) && montageHasManualDemolition && <p className="aluminium-form-error">Usuń ręcznie dodane wiersze demontażu, aby nie naliczyć usługi dwa razy.</p>}
        {(montageDemolition || montageDemolitionDisposal || montageTransportRow || montagePajaczekRow) && <div className="aluminium-service-summary">
          {(montageDemolition || montageDemolitionDisposal) && <div><span>Demontaż</span><strong>{money(montageServices.demolition, montageAnalysis.currency)}</strong></div>}
          {montageDemolitionDisposal && <div><span>Utylizacja</span><strong>{money(montageServices.disposal, montageAnalysis.currency)}</strong></div>}
          {montageTransportRow && <div><span>{TRANSPORT_OPTIONS.find(option => option.value === montageTransportType)?.label} ({montageTransportKmNumber} km × {money(montageTransportPrice)}/km)</span><strong>{money(montageTransportRow.installation_total, montageAnalysis.currency)}</strong></div>}
          {montagePajaczekRow && <div><span>Pajączek ({montagePajaczekDaysNumber} dni × {money(montagePajaczekDayRate)} + {montageTransportKmNumber} km × {money(montagePajaczekKmRate)})</span><strong>{money(montagePajaczekRow.installation_total, montageAnalysis.currency)}</strong></div>}
        </div>}
      </section>
      <h4 className="aluminium-montage-summary-title">Pierwotne kwoty przed rabatem</h4>
      <div className="aluminium-montage-financials">
        <div><span>Netto</span><strong>{money(montageFinancials.originalNet, montageAnalysis.currency)}</strong></div>
        <div><span>VAT {montageVat}%</span><strong>{money(montageFinancials.originalVat, montageAnalysis.currency)}</strong></div>
        <div><span>Brutto</span><strong>{money(montageFinancials.originalGross, montageAnalysis.currency)}</strong></div>
      </div>
      <section className="aluminium-common-installation" aria-labelledby="common-montage-installation-title">
        <div className="aluminium-common-installation-copy">
          <h4 id="common-montage-installation-title">Ogólny typ montażu</h4>
          <p>Ustaw typ montażu dla konstrukcji innych niż fasady.</p>
        </div>
        <div className="aluminium-common-installation-field">
          <label htmlFor="common-montage-installation">Wybierz typ montażu</label>
          <select id="common-montage-installation" value={commonMontageInstallation} onChange={event => changeAllMontageInstallations(event.target.value)} disabled={montageBusy || montageRowsRequiringInstallation.length === 0}>
            {montageRowsRequiringInstallation.length === 0
              ? <option value="">Nie dotyczy fasad</option>
              : !commonMontageInstallation && <option value="">Różne typy montażu</option>}
            <option value={SELECT_INSTALLATION} disabled>{SELECT_INSTALLATION}</option>
            {montageAnalysis.installations.map(item => <option key={item} value={item}>{item}</option>)}
          </select>
        </div>
      </section>
      {montageMixedConstructionWarnings.size > 0 && <section className="aluminium-mixed-construction-alert" role="alert" aria-label="Konstrukcje wymagające decyzji o sposobie montażu">
        <strong>Sprawdź konstrukcje złożone przed wyceną</strong>
        <ul>{[...montageMixedConstructionWarnings].map(([position, parts]) => <li key={position}>
          <strong>Konstrukcja nr {position}</strong> zawiera {joinConstructionParts(parts)}. Zdecyduj, czy wycenić montaż całej konstrukcji w jednym wierszu, czy osobno dla jej elementów. Przy osobnej wycenie wpisz wymiary elementów w odpowiednich wierszach.
        </li>)}</ul>
      </section>}
      <div className="aluminium-montage-table-wrap"><table className="aluminium-montage-table"><caption>Pozycje wyceny montażu</caption><thead><tr>
        <th>Pozycja</th><th>Wymiary [m]</th><th>Ilość</th><th>Pow. [m²]</th><th>Typ montażu</th><th>Rodzaj konstrukcji</th><th>Cena jedn.</th><th>Wsp. k</th><th>Cena / szt.</th><th>Suma</th><th>Akcje</th>
      </tr></thead><tbody>
        {montageDisplayRows.map((row, index, rows) => {
          const additionalIndex = rows
            .slice(0, index)
            .filter(item => (
              item.position === row.position && !item.display_position
            )).length;
          const isAdditional = Boolean(row.display_position) || additionalIndex > 0;
          const positionLabel = row.display_position
            || `${row.position}${'*'.repeat(additionalIndex)}`;
          const startsPosition = index > 0 && rows[index - 1].position !== row.position;
          const discountedRow = montageFinancials.rowsById?.[row.row_id];
          const dimensionsRequireReview = (
            !row.display_position
            && !row.automatic_service
            && montagePositionsRequiringDimensionReview.has(row.position)
            && !montageReviewedDimensionRows.has(row.row_id)
          );
          const needsManualPrice = (
            !row.automatic_service
            &&
            montageRegion === 'eu'
            && montageConfiguredPrices[`${row.installation_type}\u0000${row.construction_type}`] === undefined
            && row.construction_type !== SELECT_CONSTRUCTION
          );
          const manualPriceText = String(montageSelections[row.row_id]?.unit_price ?? '').trim();
          const manualPriceValue = Number(manualPriceText.replace(',', '.'));
          const manualPriceRequiresReview = needsManualPrice
            && (!manualPriceText || !Number.isFinite(manualPriceValue) || manualPriceValue < 0);
          return <tr key={row.row_id} className={`${startsPosition ? 'aluminium-position-start' : ''} ${row.automatic_service ? 'aluminium-service-row' : ''}`}>
          <td><strong>{positionLabel}</strong><small>str. {row.source_page}</small></td>
          <td>{row.automatic_service
            ? <span>{row.width_m} × {row.height_m}</span>
            : <><div className={`aluminium-dimensions ${dimensionsRequireReview ? 'requires-review' : ''}`} title={dimensionsRequireReview ? 'Pozycja ma kilka typów konstrukcji — sprawdź i zmień wymiary.' : undefined}><input aria-label={`Szerokość pozycji ${row.position}`} type="text" inputMode="decimal" value={row.width_m} onChange={event => changeMontageDimension(row.row_id, 'width_m', event.target.value)} disabled={montageBusy} /><span>×</span><input aria-label={`Wysokość pozycji ${row.position}`} type="text" inputMode="decimal" value={row.height_m} onChange={event => changeMontageDimension(row.row_id, 'height_m', event.target.value)} disabled={montageBusy} /></div>{dimensionsRequireReview && <small className="aluminium-dimensions-warning">Sprawdź wymiary</small>}</>}</td><td>{row.automatic_service ? row.quantity : <input className="aluminium-quantity-input" aria-label={`Ilość dla pozycji ${row.position}`} type="number" min="1" max="100" step="1" value={row.quantity} onChange={event => changeMontageQuantity(row.row_id, event.target.value)} disabled={montageBusy} />}</td><td>{new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 3 }).format(Number(row.area_m2 || 0))}</td>
          <td>{row.automatic_service
            ? 'Nie dotyczy'
            : FACADE_TYPES.has(row.construction_type)
            ? NOT_APPLICABLE
            : row.display_position
            ? <select aria-label={`Typ montażu dla pozycji ${positionLabel}`} value="not_applicable" disabled><option value="not_applicable">Nie dotyczy</option></select>
            : <select className={row.installation_type === SELECT_INSTALLATION ? 'requires-selection' : ''} value={montageSelections[row.row_id]?.installation_type || row.installation_type} onChange={event => changeMontageSelection(row.row_id, 'installation_type', event.target.value)} disabled={montageBusy}><option value={SELECT_INSTALLATION} disabled>{SELECT_INSTALLATION}</option>{montageAnalysis.installations.map(item => <option key={item}>{item}</option>)}</select>}</td>
          <td>{row.automatic_service ? row.construction_type.replace('_', ' ') : <select className={row.construction_type === SELECT_CONSTRUCTION ? 'requires-selection' : ''} value={montageSelections[row.row_id]?.construction_type || row.construction_type} onChange={event => changeMontageSelection(row.row_id, 'construction_type', event.target.value)} disabled={montageBusy}><option value={SELECT_CONSTRUCTION} disabled>{SELECT_CONSTRUCTION}</option>{montageAnalysis.constructions.map(item => <option key={item}>{item}</option>)}</select>}</td>
          <td>{needsManualPrice
            ? <input className={`aluminium-quantity-input ${manualPriceRequiresReview ? 'requires-selection' : ''}`} aria-label={`Cena jednostkowa dla pozycji ${positionLabel}`} type="text" inputMode="decimal" placeholder="PODAJ" value={montageSelections[row.row_id]?.unit_price ?? ''} onChange={event => changeMontageSelection(row.row_id, 'unit_price', event.target.value)} disabled={montageBusy} />
            : money(row.unit_price)}</td><td>{FACADE_TYPES.has(row.construction_type) || FACADE_SERVICE_TYPES.has(row.construction_type) ? '—' : new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 2 }).format(Number(row.coefficient) - 1)}</td><td>{money(discountedRow?.discountedUnit ?? row.installation_unit_price)}</td><td><strong>{money(discountedRow?.discountedTotal ?? row.installation_total)}</strong></td>
          <td><div className="aluminium-row-actions">{!row.automatic_service && <>{!row.display_position && <button type="button" className="add" aria-label={`Dodaj wiersz do pozycji ${positionLabel}`} title="Dodaj pozycję" onClick={() => addMontageRow(row)} disabled={montageBusy}>+</button>}{isAdditional && <button type="button" className="remove" onClick={() => removeMontageRow(row)} disabled={montageBusy}>Usuń</button>}</>}</div></td>
        </tr>})}
      </tbody></table></div>
      {(montageDemolition || montageDemolitionDisposal) && <div className="aluminium-montage-legend" aria-label="Legenda dodatkowych pozycji">
        <strong>Legenda pozycji:</strong>
        <span><code>_D</code> demontaż <small>(np. 1D)</small></span>
        {montageDemolitionDisposal && <span><code>_U</code> utylizacja <small>(np. 1U)</small></span>}
      </div>}
      {(montageHasUnselectedInstallation || montageHasUnselectedConstruction) && <p className="aluminium-form-error">Wybierz typ montażu i rodzaj konstrukcji we wszystkich oznaczonych wierszach, aby wyeksportować wycenę.</p>}
      <div className="aluminium-montage-discount">
        <h4>Rabatowanie montażu</h4>
        <div className="aluminium-mode-switcher" role="group" aria-label="Sposób rabatowania montażu">
          <button type="button" className={`aluminium-mode-button ${montageDiscountMode === 'general_discount' ? 'active' : ''}`} aria-pressed={montageDiscountMode === 'general_discount'} onClick={() => changeMontageDiscountMode('general_discount')}>Rabat ogólny</button>
          <button type="button" className={`aluminium-mode-button ${montageDiscountMode === 'target_gross' ? 'active' : ''}`} aria-pressed={montageDiscountMode === 'target_gross'} onClick={() => changeMontageDiscountMode('target_gross')}>Docelowa wartość brutto</button>
        </div>
        <div className="aluminium-value-controls">
          <label>VAT (%) <input style={styles.input} type="number" min="0" max="23" step="0.01" value={montageVat} onChange={event => setMontageVat(event.target.value)} /></label>
          {montageDiscountMode === 'general_discount'
            ? <label>Rabat ogólny (%) <input style={styles.input} type="number" min="0" max="100" step="0.01" value={montageGeneralDiscount} onChange={event => setMontageGeneralDiscount(event.target.value)} /></label>
            : <label>Docelowa wartość brutto <input style={styles.input} type="number" min="0.01" step="0.01" value={montageTargetGross} onChange={event => setMontageTargetGross(event.target.value)} /></label>}
        </div>
        {montageDiscountMode === 'target_gross' && localizedNumber(montageTargetGross) > montageFinancials.originalGross && <p className="aluminium-form-error">Docelowa wartość nie może być wyższa od pierwotnej wartości brutto.</p>}
        {montageDiscountMode === 'target_gross' && montageFinancials.targetBelowFixedServices && <p className="aluminium-form-error">Docelowa wartość brutto nie może być niższa od kosztu usług bez rabatu z VAT ({money(montageFinancials.fixedMinimumGross, montageAnalysis.currency)}).</p>}
        {montageDiscountMode === 'target_gross' && !montageFinancials.targetBelowFixedServices && localizedNumber(montageTargetGross) > 0 && localizedNumber(montageTargetGross) <= montageFinancials.originalGross && <p className="aluminium-montage-effective-discount">Wyliczony rabat: <strong>{montageFinancials.discount.toFixed(4)}%</strong></p>}
        <h4>Kwoty po rabacie</h4>
        {(montageDemolition || montageDemolitionDisposal || montageTransportRow || montagePajaczekRow) && <div className="aluminium-service-summary">
          {(montageDemolition || montageDemolitionDisposal) && <div><span>Demontaż po rabacie</span><strong>{money(montageFinancials.discountedDemolition, montageAnalysis.currency)}</strong></div>}
          {montageDemolitionDisposal && <div><span>Utylizacja po rabacie</span><strong>{money(montageFinancials.discountedDisposal, montageAnalysis.currency)}</strong></div>}
          {montageTransportRow && <div><span>Transport (bez rabatu)</span><strong>{money(montageFinancials.discountedTransport, montageAnalysis.currency)}</strong></div>}
          {montagePajaczekRow && <div><span>Pajączek (bez rabatu)</span><strong>{money(montageFinancials.discountedPajaczek, montageAnalysis.currency)}</strong></div>}
        </div>}
        <div className="aluminium-montage-financials discounted">
          <div><span>Netto</span><strong>{money(montageFinancials.net, montageAnalysis.currency)}</strong></div>
          <div><span>VAT {montageVat}%</span><strong>{money(montageFinancials.vat, montageAnalysis.currency)}</strong></div>
          <div><span>Brutto</span><strong>{money(montageFinancials.gross, montageAnalysis.currency)}</strong></div>
        </div>
      </div>
      <label className="aluminium-pdf-discount-option">
        <input type="checkbox" checked={montageShowDiscountInPdf} onChange={event => { setMontageShowDiscountInPdf(event.target.checked); setMontageResultUrl(''); }} disabled={montageBusy} />
        Czy rabat ma być widoczny w PDF?
      </label>
      <div className="aluminium-export-row">
        <button type="button" style={styles.button} onClick={exportMontage} disabled={montageBusy || montageExportIssues.length > 0} aria-describedby={montageExportAttempted && montageExportIssues.length ? 'montage-export-validation' : undefined}>Eksportuj wycenę do PDF</button>
        {montageExportBusy && <span className="aluminium-export-loading" role="status"><span className="aluminium-spinner" aria-hidden="true" /> Generowanie PDF…</span>}
        {montageExportAttempted && montageExportIssues.length > 0 && <div id="montage-export-validation" className="aluminium-export-validation" role="alert">
          <strong>Uzupełnij dane przed eksportem:</strong>
          <ul>{montageExportIssues.map(issue => <li key={issue}>{issue}</li>)}</ul>
        </div>}
      </div>
      <div className="aluminium-inline-preview">
        <div className="aluminium-preview-heading">
          <h3>{montagePreviewMode === 'result' && montageResultUrl ? 'Podgląd wyceny montażu' : 'Podgląd załączonej umowy'}</h3>
          {montageResultUrl && <div className="aluminium-preview-switcher">
            <button type="button" onClick={() => setMontagePreviewMode('source')} disabled={montagePreviewMode === 'source'}>Wróć do podglądu umowy</button>
            <button type="button" onClick={() => setMontagePreviewMode('result')} disabled={montagePreviewMode === 'result'}>Pokaż wycenę</button>
          </div>}
        </div>
        <iframe className="aluminium-pdf-frame" title={montagePreviewMode === 'result' && montageResultUrl ? 'Podgląd wyceny montażu PDF' : 'Podgląd umowy do wyceny montażu'} src={montagePreviewMode === 'result' && montageResultUrl ? montageResultUrl : montageSourceUrl} />
        {montageResultUrl && <div className="aluminium-result-actions">
          <a href={montageResultUrl} download={montageResultName} style={{ ...styles.button, display: 'inline-block', textDecoration: 'none' }}>Pobierz wycenę PDF</a>
          <button type="button" className="aluminium-finish-button" onClick={finish}>Zakończ</button>
        </div>}
      </div>
    </div>}

    {!montageAnalysis && montageSourceUrl && <div className="aluminium-preview-panel" style={styles.panel}><h3>Podgląd umowy — naliczanie montażu</h3><iframe className="aluminium-pdf-frame" title="Podgląd umowy do naliczania montażu" src={montageSourceUrl} /></div>}
  </>;
};

export default MontageSection;
